import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { navigationLinks } from '../data/navigationData';
import { useSchoolInfo } from '../hooks/useSchoolInfo';

export default function Header() {
  const schoolInfo = useSchoolInfo();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [expandedMobileItem, setExpandedMobileItem] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const dropdownTimeoutRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    setExpandedMobileItem(null);
  }, [location.pathname]);

  // Track scroll for subtle shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleMouseEnter = (title) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setOpenDropdown(title);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  const toggleMobileSubmenu = (title) => {
    setExpandedMobileItem(expandedMobileItem === title ? null : title);
  };

  return (
    <>
      <header
        className={`sticky top-0 z-40 transition-all duration-300 ${
          isScrolled
            ? 'bg-white shadow-md py-2.5 border-b border-neutral-200'
            : 'bg-white py-3.5 border-b border-neutral-100 shadow-xs'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Row: Logo (left) + Action CTAs / Mobile Button (right) */}
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-3 group focus:outline-none focus:ring-2 focus:ring-[#264A35] rounded-lg p-1"
              aria-label="Odder Lille Friskole Forside"
            >
              <img
                src="https://assets.auxodev.dk/assets/7e157867-df92-46f8-b5ca-20179d4d9044/odder-lille-friskole-bt.png"
                alt="Odder Lille Friskole Logo"
                className="h-14 sm:h-16 lg:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-102"
              />
            </Link>

            {/* Desktop Action CTAs (>= 1200px) */}
            <div className="hidden min-[1200px]:flex items-center gap-3">
              <Link
                to="/besoeg-skolen"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-[#264A35] bg-[#eef4e6] hover:bg-[#e2ecd8] transition-all duration-200 border border-[#d9e5d4] shadow-xs hover:shadow-sm"
              >
                <i className="fa-regular fa-compass text-xs" aria-hidden="true" />
                <span>Besøg skolen</span>
              </Link>

              <Link
                to="/skriv-dit-barn-op"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-[#1D2923] bg-[#c1d8e5] hover:bg-[#aecad9] shadow-sm hover:shadow transition-all transform active:scale-98"
              >
                <i className="fa-solid fa-pen-nib text-xs" aria-hidden="true" />
                <span>Skriv dit barn op</span>
              </Link>
            </div>

            {/* Mobile Menu Button (< 1200px) */}
            <div className="flex items-center gap-2 min-[1200px]:hidden">
              <Link
                to="/skriv-dit-barn-op"
                className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-[#1D2923] bg-[#c1d8e5] hover:bg-[#aecad9]"
              >
                <span>Skriv dit barn op</span>
              </Link>

              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-[#1D2923] hover:text-[#264A35] transition-colors focus:outline-none"
                aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
                aria-expanded={mobileMenuOpen}
              >
                <i
                  className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          {/* Bottom Row: Left-aligned Navigation Links under Logo (>= 1200px) */}
          <div className="hidden min-[1200px]:flex items-center justify-start pt-2.5 mt-2 border-t border-neutral-100">
            <nav
              className="flex items-center gap-1.5 -ml-2"
              aria-label="Hovednavigation"
            >
              {navigationLinks.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isChildActive =
                  hasChildren &&
                  item.children.some((child) => location.pathname === child.href);
                const isDirectActive = location.pathname === item.href;
                const isActive = isDirectActive || isChildActive;

                if (!hasChildren) {
                  return (
                    <NavLink
                      key={item.title}
                      to={item.href}
                      className={({ isActive: directActive }) =>
                        `px-3.5 py-1.5 rounded-full text-sm font-semibold tracking-wide transition-colors ${
                          directActive
                            ? 'text-[#264A35] font-bold'
                            : 'text-[#2D3A32] hover:text-[#264A35]'
                        }`
                      }
                    >
                      {item.title}
                    </NavLink>
                  );
                }

                return (
                  <div
                    key={item.title}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter(item.title)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setOpenDropdown(openDropdown === item.title ? null : item.title)
                      }
                      aria-expanded={openDropdown === item.title}
                      className={`px-3.5 py-1.5 rounded-full text-sm font-semibold tracking-wide transition-colors flex items-center gap-1.5 ${
                        isActive
                          ? 'text-[#264A35] font-bold'
                          : 'text-[#2D3A32] hover:text-[#264A35]'
                      }`}
                    >
                      <span>{item.title}</span>
                      <i
                        className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                          openDropdown === item.title ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                      />
                    </button>

                    {/* Dropdown Menu */}
                    {openDropdown === item.title && (
                      <div className="absolute left-0 top-full pt-2 min-w-[220px] z-50 animate-fadeIn">
                        <div className="bg-white rounded-2xl shadow-xl border border-neutral-200/80 p-1.5 overflow-hidden">
                          {item.children.map((child) => {
                            const isCurrent = location.pathname === child.href;
                            return (
                              <Link
                                key={child.href}
                                to={child.href}
                                className="block px-3.5 py-2 transition-all group"
                              >
                                <div
                                  className={`text-sm font-semibold transition-colors ${
                                    isCurrent
                                      ? 'text-[#264A35] font-bold'
                                      : 'text-[#1D2923] group-hover:text-[#264A35]'
                                  }`}
                                >
                                  {child.title}
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer (< 1200px) */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 min-[1200px]:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-[#1D2923]/40 backdrop-blur-xs transition-opacity"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Sliding Panel */}
          <div className="fixed inset-y-0 right-0 max-w-sm w-full bg-[#f8faf5] shadow-2xl flex flex-col z-10 border-l border-[#d9e5d4] overflow-y-auto">
            {/* Mobile Header */}
            <div className="p-4 border-b border-[#d9e5d4] flex items-center justify-between">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center"
              >
                <img
                  src="https://assets.auxodev.dk/assets/7e157867-df92-46f8-b5ca-20179d4d9044/odder-lille-friskole-bt.png"
                  alt="Odder Lille Friskole Logo"
                  className="h-10 w-auto"
                />
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-[#1D2923] hover:text-black transition-colors focus:outline-none"
                aria-label="Luk menu"
              >
                <i className="fa-solid fa-xmark text-2xl" aria-hidden="true" />
              </button>
            </div>

            {/* Mobile Actions Banner */}
            <div className="p-4 bg-[#eef4e6] border-b border-[#d9e5d4] space-y-2">
              <Link
                to="/skriv-dit-barn-op"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-bold text-[#1D2923] bg-[#c1d8e5] hover:bg-[#aecad9] shadow-sm text-center"
              >
                <i className="fa-solid fa-pen-nib text-xs" aria-hidden="true" />
                <span>Skriv dit barn op</span>
              </Link>
              <Link
                to="/besoeg-skolen"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold text-[#264A35] bg-white hover:bg-[#f8faf5] border border-[#d9e5d4] text-center"
              >
                <i className="fa-regular fa-compass text-xs" aria-hidden="true" />
                <span>Besøg skolen</span>
              </Link>
            </div>

            {/* Mobile Navigation List */}
            <nav className="p-4 flex-1 space-y-1" aria-label="Mobil navigation">
              {navigationLinks.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedMobileItem === item.title;

                if (!hasChildren) {
                  return (
                    <NavLink
                      key={item.title}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center justify-between px-4 py-3 text-base font-semibold transition-colors ${
                          isActive
                            ? 'text-[#264A35] font-bold'
                            : 'text-[#1D2923] hover:text-[#264A35]'
                        }`
                      }
                    >
                      <span>{item.title}</span>
                    </NavLink>
                  );
                }

                return (
                  <div key={item.title} className="rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleMobileSubmenu(item.title)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-base font-semibold transition-colors ${
                        isExpanded
                          ? 'text-[#264A35] font-bold'
                          : 'text-[#1D2923] hover:text-[#264A35]'
                      }`}
                    >
                      <span>{item.title}</span>
                      <i
                        className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                          isExpanded ? 'rotate-180 text-[#264A35]' : 'text-[#6A7B72]'
                        }`}
                        aria-hidden="true"
                      />
                    </button>

                    {isExpanded && (
                      <div className="pl-4 pr-2 py-2 space-y-1 bg-[#f1f6ed] border-l-2 border-[#264A35] my-1 rounded-r-xl">
                        {item.children.map((child) => {
                          const isCurrent = location.pathname === child.href;
                          return (
                            <Link
                              key={child.href}
                              to={child.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                                isCurrent
                                  ? 'font-bold text-[#264A35]'
                                  : 'text-[#3E4E45] hover:text-[#264A35]'
                              }`}
                            >
                              <div>{child.title}</div>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Mobile Contact Quick Footer */}
            <div className="p-4 border-t border-[#d9e5d4] bg-[#f8faf5] text-xs text-[#5A6961] space-y-2">
              <div className="font-semibold text-[#1D2923]">{schoolInfo.name}</div>
              <div>{schoolInfo.address}, {schoolInfo.city}</div>
              <div className="flex flex-col gap-1 pt-1">
                <a
                  href={`tel:${schoolInfo.phoneRaw}`}
                  className="inline-flex items-center gap-2 text-[#264A35] font-semibold whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                  <span>{schoolInfo.phone}</span>
                </a>
                <a
                  href={`mailto:${schoolInfo.email}`}
                  className="inline-flex items-center gap-2 text-[#264A35] font-semibold whitespace-nowrap"
                >
                  <i className="fa-solid fa-envelope text-xs" aria-hidden="true" />
                  <span>{schoolInfo.email}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
